import { ErrorMessage, Field, Form, Formik, FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { ButtonCustom } from "../../../../components/custom/button-custom";
import { ModalCustom } from "../../../../components/custom/modal-custom";

import { showToast } from "../../../../components/custom/custom-toast";
import { LoadingCustom } from "../../../../components/custom/loading-custom";
import { useFetchData } from "../../../../hooks/useFetch";
import { CompaniesResponse } from "../../../../models/response/companies";
import { API_COMPANIES_ROUTER } from "../../../../services/api-config";

interface CompaniesUpdateProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  setRefresh: (value: boolean) => void;
  dataObject: CompaniesResponse;
}

export const CompaniesUpdate = ({ isOpen, setIsOpen, setRefresh, dataObject }: CompaniesUpdateProps) => {
  const formikRef = useRef<FormikProps<any> | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const { request: fetchDataUpdate, baseResponse: resUpdate } = useFetchData.Post();

  useEffect(() => {
    if (!isOpen) formikRef.current?.resetForm();
  }, [isOpen]);

  const handleUpdate = (values: any) => {
    fetchDataUpdate({
      endPoint: API_COMPANIES_ROUTER.API_UPDATE_COMPANIES(dataObject._id),
      data: {
        name: values.name,
        phone: values.phone,
        address: values.address,
        description: values.description,
      },
    });
  };

  useEffect(() => {
    if (!resUpdate) return;
    if (resUpdate.status === 200) {
      showToast({ message: "Chỉnh sửa thành công", type: "success" });
      setIsOpen(false);
      setRefresh(true);
    } else {
      showToast({ message: resUpdate.message, type: "error" });
    }
  }, [resUpdate]);

  useEffect(() => {
    if (dataObject._id !== "") setIsDataLoaded(true);
  }, [dataObject]);

  if (!isDataLoaded) return <LoadingCustom />;

  const SubmittedForm = Yup.object().shape({
    name: Yup.string()
      .min(2, "Độ dài tối thiểu 2 ký tự")
      .max(100, "Độ dài tối đa 100 ký tự")
      .required("Độ dài tối thiểu 2 ký tự"),
    phone: Yup.string().min(10, "Số điện thoại chưa đúng 10 hoặc 11 ký tự"),
    address: Yup.string().max(100, "Độ dài tối đa 100 ký tự"),
  });

  return (
    <Formik
      innerRef={formikRef}
      initialValues={dataObject}
      validationSchema={SubmittedForm}
      onSubmit={(values) => {
        handleUpdate(values);
      }}
    >
      {({ values, submitForm, setFieldValue }) => {
        const bodyModal = () => {
          return (
            <Form className="space-y-5">
              <div className="grid grid-cols-1  gap-5">
                <div>
                  <label>
                    Tên công ty <span className="text-red-500">*</span>
                  </label>
                  <Field
                    value={values.name}
                    name="name"
                    type="text"
                    id="name"
                    placeholder="Nhập thông tin"
                    className="form-input"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue("name", e.target.value);
                    }}
                  />
                  <ErrorMessage name="name" component="div" className="text-red-500 text-xs mt-1" />
                </div>
                <div>
                  <label htmlFor="phone">Số điện thoại</label>
                  <Field
                    data-testid="phone-input"
                    value={values.phone}
                    name="phone"
                    type="text"
                    id="phone"
                    placeholder="Nhập thông tin"
                    className="form-input"
                    minLength={10}
                    maxLength={11}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let inputValue = e.target.value.replace(/\D/g, "");
                      if (inputValue.length > 0 && !inputValue[0].startsWith("0")) inputValue = "0" + inputValue;
                      if (inputValue.length <= 11) setFieldValue("phone", inputValue);
                    }}
                  />
                  <ErrorMessage name="phone" component="div" className="text-red-500 text-xs mt-1" />
                </div>
                <div>
                  <label htmlFor="address">Địa chỉ</label>
                  <Field
                    value={values.address}
                    name="address"
                    type="text"
                    id="address"
                    maxLength={100}
                    placeholder="Nhập thông tin"
                    className="form-input"
                  />
                  <ErrorMessage name="address" component="div" className="text-red-500 text-xs mt-1" />
                </div>
                <div>
                  <label htmlFor="description">Mô tả</label>
                  <Field name="description">
                    {({ field }: any) => (
                      <textarea
                        {...field}
                        id="description"
                        rows={3}
                        className="form-textarea"
                        placeholder="Nhập thông tin"
                        required
                        maxLength={255}
                        value={values.description}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                          setFieldValue("description", e.target.value);
                        }}
                      />
                    )}
                  </Field>
                </div>
              </div>
            </Form>
          );
        };

        const footerModal = () => <ButtonCustom type="button" text="Lưu" onClick={submitForm} />;

        return (
          <ModalCustom
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            titleModal="Chỉnh sửa công ty"
            bodyModal={bodyModal()}
            footerModal={footerModal()}
            modalSize="40%"
          />
        );
      }}
    </Formik>
  );
};
